import { useSelector } from 'react-redux'
import { useEffect, useState } from 'react'

import { EVENTS, trackEvent } from 'utils/tracking/events-tracker'
import { useRecurly } from 'lib/recurly'
import { CHECKOUT_ERRORS } from 'utils/consts.mjs'
import { useThreeDSecure } from 'context/three-d-secure-context'
import { useGrowthBookFeature } from 'context/growthbook-context'
import { growthbookFeatures } from 'utils/growthbook'

import { PrimaryButton } from '../../../buttons/Buttons'
import { translate } from '../../../../utils/translation'
import { selectCurrentUser } from '../../../../state/selectors'
import { Mixpanel, MIXPANEL_PROPS } from '../../../../utils/mixpanel'
import { isSubUser, shouldUpdateBillingInfo } from '../../../../utils/userAuth'
import ReachedText from '../common/ReachedText'
import Title from '../common/Title'
import Alert from '../common/Alert'
import Icon from '../../../icon/Icon'
import warning from '../../../../styles/assets/warning-icon.svg'
import ReachedTextContainer from '../common/ReachedTextContainer'

import PlanPriceSelector from './PlanPriceSelector'
import PlanSlider from './PlanSlider'

const limits = {
  tier1: {
    keywords: 125,
    competitors: 5,
  },
  tier2: {
    keywords: 150,
    competitors: 10,
  },
  tier3: {
    keywords: 300,
    competitors: 15,
  },
}

export default ({
  interval,
  tier,
  onIntervalChange,
  onTierChange,
  onContinue,
  onUpgrade,
  title,
  reachedText,
  maxLimitReachedText,
  promotionLabel,
  featureValue,
  onScrollToTop,
  loggedIn,
}) => {
  const { isLoading: isNewCheckoutPlanSelectionLoading, value } =
    useGrowthBookFeature(growthbookFeatures.NEW_CHECKOUT_PLAN_SELECTION_V2)

  const [serverError, setServerError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const user = useSelector(selectCurrentUser())
  const currentTier = user?.subscription?.tier
  const currentInterval = user?.subscription?.planInterval
  const shouldContinue = !user.subscription || shouldUpdateBillingInfo()

  const recurly = useRecurly()
  const { updateThreeDAuth } = useThreeDSecure()

  useEffect(() => {
    // NEED TO WAIT FOR SUPERPROPERTY
    setTimeout(() => {
      if (loggedIn && !isNewCheckoutPlanSelectionLoading) {
        if (value?.experiment !== 'others') {
          trackEvent({
            event: value?.enabled
              ? EVENTS.NEW_CHECKOUT_MODAL_VIEW_VARIANT
              : EVENTS.NEW_CHECKOUT_MODAL_VIEW_CONTROL,
          })
        }
        Mixpanel.trackChooseYourPlanView({
          source: MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL,
        })
      }
    }, 1000)
  }, [
    loggedIn,
    isNewCheckoutPlanSelectionLoading,
    value?.experiment,
    value?.enabled,
  ])

  const getTitleText = () => {
    if (!title) {
      return translate('choose_plan')
    }

    if (title === 'heading_new_keywords') {
      return translate(title, limits[tier].keywords)
    }

    if (title === 'heading_new_competitors') {
      return translate(title, limits[tier].competitors)
    }

    if (title === 'heading_new_location' && tier === 'tier1') {
      return translate(title)
    }

    if (title === 'heading_new_location') {
      return translate('step2_upgrade_subscription_unlimited_locations')
    }

    return translate('choose_plan')
  }

  let buttonText

  if (tier === currentTier && interval === currentInterval) {
    buttonText = translate('current_plan')
  } else if (shouldContinue) {
    buttonText = translate('continue_button')
  } else {
    buttonText = translate('confirm_button')
  }

  let observer
  const addObserver = () => {
    observer = new MutationObserver(() => {
      const iframe = document.querySelector(
        `#checkout-3d-auth-container > div > iframe`,
      )
      if (iframe && iframe.getAttribute('scrolling') === 'no') {
        iframe.setAttribute('scrolling', 'yes')
      }
    })

    observer.observe(document.querySelector('#checkout-3d-auth-container'), {
      subtree: true,
      childList: true,
    })
  }

  const removeObserver = () => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
  }

  const handle3DError = (error) => {
    document.querySelector('#checkout-3d-auth-container').replaceChildren()
    updateThreeDAuth(true)
    onScrollToTop()

    const { description } = error

    const risk = recurly.Risk()
    const threeDSecure = risk.ThreeDSecure({
      actionTokenId: description?.three_d_secure_action_token_id,
    })

    threeDSecure.on('token', (threeDToken) => {
      removeObserver()
      updateThreeDAuth(false)
      return onUpgrade(() => {
        setServerError(translate('three_d_verification_failed'))
      }, threeDToken)
    })

    threeDSecure.on('error', (threeDError) => {
      console.error(threeDError)
      removeObserver()
      updateThreeDAuth(false)
      setServerError({
        message: translate('three_d_verification_failed'),
      })
      setIsLoading(false)
    })

    addObserver()

    threeDSecure.attach(document.querySelector('#checkout-3d-auth-container'))
  }

  return (
    <>
      {reachedText && (
        <ReachedTextContainer>
          <Icon size="20px" src={warning} />
          <ReachedText>
            {translate(reachedText, maxLimitReachedText)}
          </ReachedText>
        </ReachedTextContainer>
      )}
      <Title
        style={{ marginBottom: '10px' }}
        dangerouslySetInnerHTML={{
          __html: getTitleText(),
        }}
      />
      {serverError && (
        <Alert onClose={() => setServerError('')}>{serverError}</Alert>
      )}
      <PlanSlider tier={tier} onChange={onTierChange} />
      <PlanPriceSelector
        promotionLabel={promotionLabel}
        featureValue={featureValue}
        interval={interval}
        tier={tier}
        onChange={onIntervalChange}
      />
      <PrimaryButton
        tag={buttonText}
        theme="orange"
        onClick={() => {
          setServerError('')
          trackEvent({
            event: EVENTS.PLAN_SELECT_CLICK,
            mixpanelProperties: [
              {
                tier,
                planInterval: interval,
                source: MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL,
              },
            ],
          })
          if (shouldContinue) {
            onContinue()
          } else {
            setIsLoading(true)
            onUpgrade((err) => {
              if (err) {
                if (
                  err.code === CHECKOUT_ERRORS.THREE_D_SECURE_ACTION_REQUIRED
                ) {
                  handle3DError(err)
                  setIsLoading(false)
                  return
                }
                setServerError(translate('card_error'))
              }

              setIsLoading(false)
            })
          }
        }}
        width="100%"
        fontSize="14px"
        letterSpacing="0.08em"
        style={{
          lineHeight: 1,
          marginTop: '10px',
          flexShrink: 0,
        }}
        disabled={
          (tier <= currentTier &&
            (currentInterval === 'lifetime' || currentInterval === 'yearly')) ||
          (tier === currentTier && currentInterval === interval) ||
          !tier ||
          !interval ||
          isSubUser() ||
          isLoading
        }
        isLoading={isLoading}
      />
    </>
  )
}
